/**
 * ScrollySteps — "How it builds up": tabs only, no scroll sync.
 * Left: 3 step cards (buttons). Right: same app window frame, content changes by active step.
 * Click sets activeStep only; no scrollIntoView, no anchor, no scroll-driven logic.
 */

import * as React from "react";
import {
  motion,
  useMotionValue,
  useTransform,
  useSpring,
  AnimatePresence,
  useReducedMotion,
  LayoutGroup,
} from "framer-motion";
import { cn } from "@/lib/utils";
import { BrowserFrame } from "@/components/landing/BrowserFrame";
import { LandingScaledPreview, type CanvasCallout } from "@/components/landing/LandingScaledPreview";
import { TextReveal } from "@/components/landing/motion/TextReveal";
import { TodayPreview } from "@/components/landing/previews/TodayPreview";
import { LeadImportPreview } from "@/components/landing/previews/LeadImportPreview";
import { RevenueFlowPreview } from "@/components/landing/previews/RevenueFlowPreview";
import { useCoarsePointer } from "@/components/landing/hooks/useCoarsePointer";
import { DemoInteractionGuard } from "@/components/landing/demos/DemoInteractionGuard";

export type ScrollyStep = {
  key: string;
  stepLabel: string;
  title: string;
  desc: string;
  bullets?: string[];
  PreviewComponent: React.ComponentType;
  badge?: string;
  baseW: number;
  baseH: number;
  callouts?: CanvasCallout[];
};

const STEPS: ScrollyStep[] = [
  {
    key: "import",
    stepLabel: "Step 1",
    title: "Import / Leads come in",
    desc: "Bring leads in via CSV or connect Meta. Today can decide what to do once they're in.",
    PreviewComponent: LeadImportPreview,
    badge: "Leads",
    baseW: 980,
    baseH: 420,
    callouts: [
      { x: 220, y: 120, label: "Import CSV", toX: 320, toY: 140 },
      { x: 220, y: 200, label: "Connect Meta", toX: 320, toY: 220 },
    ],
  },
  {
    key: "prioritizes",
    stepLabel: "Step 2",
    title: "One thing at a time",
    desc: "Lock focus on the top priority. Call, WhatsApp, or send to phone — one click.",
    PreviewComponent: TodayPreview,
    badge: "Today",
    baseW: 980,
    baseH: 620,
    callouts: [
      { x: 220, y: 220, label: "Top priority", toX: 320, toY: 240 },
      { x: 800, y: 320, label: "Call / WhatsApp", toX: 700, toY: 300 },
    ],
  },
  {
    key: "execution",
    stepLabel: "Step 3",
    title: "Execution becomes automatic",
    desc: "Decision Sales OS removes bad decisions from the equation so you just execute.",
    PreviewComponent: RevenueFlowPreview,
    badge: "Revenue Flow",
    baseW: 980,
    baseH: 680,
    callouts: [
      { x: 490, y: 200, label: "In play", toX: 490, toY: 260 },
      { x: 490, y: 480, label: "Won / Lost", toX: 490, toY: 420 },
    ],
  },
];

const transition = { duration: 0.18, ease: [0, 0, 0.2, 1] as [number, number, number, number] };
const previewTransition = { duration: 0.22, ease: [0, 0, 0.2, 1] as [number, number, number, number] };

const FRAME_HEIGHT = "h-[480px] md:h-[520px] lg:h-[560px]";

const cardBase =
  "group relative grid grid-cols-[36px_1fr] gap-4 items-start rounded-2xl border p-5 lg:p-6 backdrop-blur transition duration-[0.18s] ease-[cubic-bezier(0,0,0.2,1)] bg-white/60 dark:bg-white/[0.03]";
const cardInactive =
  "border-slate-200/70 hover:border-slate-300/80 dark:border-white/10 dark:hover:border-white/15 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.12)] dark:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.35)]";
const cardActive =
  "border-primary/25 ring-1 ring-primary/15 shadow-[0_12px_40px_-24px_rgba(2,6,23,0.35),0_0_0_1px_rgba(59,130,246,0.08)] dark:border-primary/30 dark:ring-primary/20 dark:shadow-[0_24px_60px_-24px_rgba(0,0,0,0.45)]";

type ScrollyStepsProps = {
  className?: string;
  id?: string;
};

export function ScrollySteps({ className, id = "how-it-works" }: ScrollyStepsProps) {
  const reduce = useReducedMotion();
  const coarse = useCoarsePointer();
  const [activeIndex, setActiveIndex] = React.useState(0);
  const frameRef = React.useRef<HTMLDivElement>(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const cursorRotateX = useSpring(useTransform(my, [-0.5, 0.5], [2, -2]), { stiffness: 120, damping: 20 });
  const cursorRotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-3, 3]), { stiffness: 120, damping: 20 });

  function onFrameMove(e: React.MouseEvent) {
    if (coarse || reduce) return;
    const el = frameRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    mx.set(x);
    my.set(y);
  }

  function onFrameLeave() {
    mx.set(0);
    my.set(0);
  }

  const handleStepClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveIndex(index);
  };

  const step = STEPS[activeIndex];
  const Preview = step?.PreviewComponent;

  return (
    <section
      id={id}
      className={cn(
        "relative overflow-visible scroll-mt-28 border-t border-slate-200/70 py-24 lg:py-28 dark:border-white/10",
        className
      )}
    >
      <div className="relative overflow-visible">
        <div
          className="pointer-events-none absolute inset-0 -z-10 dark:hidden"
          aria-hidden
          style={{
            background:
              "radial-gradient(60% 60% at 50% 0%, rgba(59,130,246,0.10), transparent 70%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 -z-10 hidden dark:block"
          aria-hidden
          style={{
            background:
              "radial-gradient(60% 60% at 50% 0%, rgba(59,130,246,0.14), transparent 70%)",
          }}
        />
      </div>
      <div className="mx-auto max-w-6xl px-6 overflow-visible">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 overflow-visible">
          {/* Left: step cards — tabs, click only (no scroll) */}
          <div className="min-w-0 space-y-6">
            <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] leading-[var(--leading-headline)] text-slate-950 dark:text-white">
              How it builds up
            </h2>
            <LayoutGroup>
              <div
                role="tablist"
                aria-label="How it builds up steps"
                className="space-y-6"
              >
                {STEPS.map((s, index) => {
                  const isActive = activeIndex === index;
                  return (
                    <motion.button
                      key={s.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`step-panel-${index}`}
                      id={`step-tab-${index}`}
                      tabIndex={isActive ? 0 : -1}
                      onClick={(e) => handleStepClick(e, index)}
                      initial={reduce ? false : { opacity: 0.6, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={transition}
                      className={cn(
                        cardBase,
                        "w-full cursor-pointer text-left",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2",
                        "hover:border-slate-300/80 dark:hover:border-white/20",
                        isActive ? cardActive : cardInactive,
                        "overflow-visible"
                      )}
                    >
                      {isActive && (
                        <motion.div
                          layoutId="step-active-indicator"
                          className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl bg-primary dark:bg-primary"
                          transition={transition}
                          aria-hidden
                        />
                      )}
                      <div
                        className={cn(
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200/80 bg-slate-100/90 text-[13px] font-medium leading-none text-slate-600 transition duration-150 ease-[cubic-bezier(0,0,0.2,1)] dark:border-white/15 dark:bg-white/[0.04] dark:text-white/80",
                          isActive &&
                            "ring-2 ring-primary/30 bg-primary/10 text-primary dark:ring-primary/40 dark:bg-primary/15 dark:text-primary dark:border-primary/20"
                        )}
                        aria-hidden
                      >
                        {index + 1}
                      </div>
                      <div className="min-w-0 space-y-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                          {s.stepLabel}
                        </span>
                        <h3 className="leading-tight text-lg font-semibold text-slate-950 dark:text-white mt-0.5">
                          <TextReveal text={s.title} animate={isActive} />
                        </h3>
                        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                          {s.desc}
                        </p>
                        {s.bullets?.length ? (
                          <ul className="space-y-1.5 pt-1 text-sm text-slate-600 dark:text-slate-400">
                            {s.bullets.map((b, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <span className="h-1 w-1 shrink-0 rounded-full bg-primary" />
                                {b}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </LayoutGroup>
          </div>

          {/* Right: same frame, content by step — no sticky scroll, no parallax */}
          <div className="relative z-10 flex items-center justify-center">
            <div className="w-full max-w-[540px] mx-auto lg:mx-0 flex items-center justify-center">
              <motion.div
                ref={frameRef}
                className="w-full"
                onMouseMove={onFrameMove}
                onMouseLeave={onFrameLeave}
                style={{
                  rotateX: !coarse && !reduce ? cursorRotateX : 0,
                  rotateY: !coarse && !reduce ? cursorRotateY : 0,
                  transformStyle: "preserve-3d",
                  perspective: 1000,
                }}
                data-demo-guard
              >
                <DemoInteractionGuard>
                  <BrowserFrame badge={step?.badge} className="w-full">
                    <div
                      className={cn(
                        "relative overflow-hidden rounded-b-2xl border border-white/5 bg-[#0b0d10]",
                        "shadow-[0_24px_64px_-24px_rgba(0,0,0,0.55)]",
                        FRAME_HEIGHT
                      )}
                    >
                      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-white/10 to-transparent z-[1]" aria-hidden />
                      <div className="absolute inset-0 overflow-hidden">
                        <div className="h-full w-full flex items-center justify-center p-2 relative">
                          <AnimatePresence mode="wait" initial={false}>
                            {Preview && step ? (
                              <motion.div
                                key={step.key}
                                initial={
                                  reduce
                                    ? { opacity: 1, y: 0 }
                                    : { opacity: 0, y: 10, filter: "blur(2px)" }
                                }
                                animate={
                                  reduce
                                    ? { opacity: 1, y: 0 }
                                    : { opacity: 1, y: 0, filter: "blur(0px)" }
                                }
                                exit={
                                  reduce
                                    ? { opacity: 0 }
                                    : { opacity: 0, y: -10, filter: "blur(2px)" }
                                }
                                transition={
                                  reduce
                                    ? { duration: 0.1 }
                                    : { duration: 0.22, ease: [0, 0, 0.2, 1] }
                                }
                                className="absolute inset-0 flex items-center justify-center"
                              >
                                <LandingScaledPreview
                                  baseW={step.baseW}
                                  baseH={step.baseH}
                                  callouts={step.callouts}
                                >
                                  <Preview />
                                </LandingScaledPreview>
                              </motion.div>
                            ) : null}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>
                  </BrowserFrame>
                </DemoInteractionGuard>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
